import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "@/services/userService";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | guest
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const u = await authService.session();
      setUser(u || null);
      setStatus(u ? "authed" : "guest");
      return u || null;
    } catch (_) {
      setUser(null);
      setStatus("guest");
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth -> AuthCallback exchanges session first.
    if (window.location.hash?.includes("session_id=")) {
      setStatus("loading");
      return;
    }
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      status,
      isAdmin: user?.role === "admin",
      refresh,
      setSessionUser: (u) => {
        setUser(u);
        setStatus(u ? "authed" : "guest");
      },
      login: async (email, password) => {
        const u = await authService.login(email, password);
        setUser(u);
        setStatus("authed");
        return u;
      },
      register: async (name, email, password) => {
        const u = await authService.register(name, email, password);
        setUser(u);
        setStatus("authed");
        return u;
      },
      logout: async () => {
        try {
          await authService.logout();
        } catch (_) {}
        setUser(null);
        setStatus("guest");
        qc.removeQueries({ queryKey: ["watchlist"] });
        qc.removeQueries({ queryKey: ["watchlist-ids"] });
        qc.removeQueries({ queryKey: ["continue"] });
      },
      updateUser: (u) => setUser(u),
    }),
    [user, status, refresh, qc]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
